import Modal from "@/Components/Modal";
import CloseIcon from "@/Components/Icons/CloseIcon";
import React, {useEffect, useMemo, useRef, useState} from "react";
import PrimaryButton from "@/Components/PrimaryButton";
import Spinner from "@/Components/Spinner";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {getLanguageName, isRtlLanguage} from "@/utils/textFormatting";
import {JobUpload, PersistedExtractionJob, SubtitleBurnInJob, SubtitleFormat} from "@/types";
import Select from "@/Components/Select";
import VideoExportTab from "@/Pages/Subtitle/Partials/VideoExportTab";
import {VideoPlayerRef} from "@/Pages/Subtitle/Partials/VideoPlayer";
import {useIsConfirmOpen} from "@/Components/ConfirmDialog";
import Popover from "@/Components/Popover";
import InfoIcon from "@/Components/Icons/InfoIcon";

interface DownloadOption {
    format: SubtitleFormat;
    label: string;
    description: string;
}

const downloadOptions: DownloadOption[] = [
    {
        format: 'srt',
        label: 'SRT',
        description: 'SubRip',
    },
    {
        format: 'vtt',
        label: 'VTT',
        description: 'WebVTT',
    },
    {
        format: 'txt',
        label: 'TXT',
        description: 'Plain text',
    },
    {
        format: 'ass',
        label: 'ASS',
        description: 'Substation Alpha',
    },
    {
        format: 'json',
        label: 'JSON',
        description: 'Raw data',
    }
];

type ExportTab = 'subtitle' | 'video';

type BaseExportModalProps = {
    show: boolean;
    onClose: () => void;
    onDownload: (format: SubtitleFormat, languages: string[], options: Record<string, any>) => Promise<void>;
    onBurnInStart?: (job: SubtitleBurnInJob) => void;
    availableLanguages: Record<number, string>,
    defaultLanguage?: string,
    isBulk: boolean
    hasStyles?: boolean;
    initialUpload: JobUpload | null;
    isFreeTrial: boolean;
}

type ExportModalProps = BaseExportModalProps & (
    | {
        videoPlayerRef?: never;
        job?: never;
        onUploadComplete?: () => never;
    }
    | {
        videoPlayerRef: VideoPlayerRef | null;
        job: PersistedExtractionJob;
        onUploadComplete: () => void;
    }
);

export default function ExportModal({
    show,
    onClose,
    onBurnInStart,
    onDownload,
    availableLanguages,
    defaultLanguage,
    isBulk,
    hasStyles = false,
    initialUpload,
    videoPlayerRef,
    job,
    isFreeTrial,
    onUploadComplete
}: ExportModalProps) {
    const [activeTab, setActiveTab] = useState<ExportTab>('subtitle');
    const [videoTabSeen, setVideoTabSeen] = useState(() => {
        return typeof localStorage !== 'undefined' && localStorage.getItem('video-export-tab-seen') === 'true';
    });
    const [selectedFormat, setSelectedFormat] = useState<SubtitleFormat>('srt');
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
    const [includeTimestamps, setIncludeTimestamps] = useState(false);
    const [closeSmallGaps, setCloseSmallGaps] = useState(false);
    const [embedFonts, setEmbedFonts] = useState(true);
    const [rtlEmbedding, setRtlEmbedding] = useState(() => {
        return typeof localStorage === 'undefined' || localStorage.getItem('subtitles-rtl-embedding') !== 'false';
    });
    const [lineSpacing, setLineSpacing] = useState(2);
    const [showOptions, setShowOptions] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const languageOptions = useMemo(() => Object.values(availableLanguages), [availableLanguages]);
    const handleCloseRef = useRef<(() => Promise<boolean>) | null>(null);
    const isConfirmOpen = useIsConfirmOpen();

    useEffect(() => {
        const format = localStorage.getItem('subtitles-download-format');
        if (format) {
            setSelectedFormat(format as SubtitleFormat);
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('subtitles-download-format', selectedFormat);
    }, [selectedFormat]);

    useEffect(() => {
        setSelectedLanguages([]);
    }, [show]);

    const showRtlEmbeddingOption = useMemo(() => {
        if (!['srt', 'vtt'].includes(selectedFormat)) {
            return false;
        }
        const languages = selectedLanguages.length ? selectedLanguages : [defaultLanguage ?? 'original'];
        return languages.includes('original') || languages.some(isRtlLanguage);
    }, [selectedFormat, selectedLanguages, defaultLanguage]);

    const handleRtlEmbeddingChange = (checked: boolean) => {
        setRtlEmbedding(checked);
        localStorage.setItem('subtitles-rtl-embedding', String(checked));
    }

    const handleClose = async () => {
        if (isConfirmOpen) {
            return;
        }
        const shouldClose = await handleCloseRef.current?.() ?? true;
        if (shouldClose) {
            onClose();
        }
    }

    const handleDownload = async () => {
        setIsDownloading(true);
        const options = getOptions();
        try {
            await onDownload(selectedFormat, selectedLanguages, options);
        } finally {
            setIsDownloading(false);
        }
        onClose();
    }

    const getOptions = () => {
        const rtlOptions = showRtlEmbeddingOption ? { rtlEmbedding } : {};
        if (selectedFormat === 'txt') {
            return { includeTimestamps, lineSpacing }
        } else if (selectedFormat === 'ass') {
            return { closeSmallGaps, embedFonts }
        } else if (selectedFormat === 'json') {
            return {}
        } else {
            return { closeSmallGaps, ...rtlOptions }
        }
    }

    return (
        <Modal maxWidth="md" show={show} onClose={handleClose}>
            <div id="export-modal" className="w-full">
                <div className="flex items-center pt-4 px-5">
                    <div className="w-8 h-8"></div>
                    <div className="flex-grow flex justify-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Export</h3>
                    </div>
                    <button type="button"
                            onClick={handleClose}
                            className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                            data-modal-hide="export-modal">
                        <CloseIcon/>
                        <span className="sr-only">Close modal</span>
                    </button>
                </div>

                {/* Tab Toggle - only show when video export is available */}
                {videoPlayerRef && (
                    <div className="px-7 pt-4 mb-2">
                        <div className="flex border-b border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setActiveTab('subtitle')}
                                className={`flex-1 py-2 px-4 text-sm font-medium transition-all border-b-2 -mb-px ${
                                    activeTab === 'subtitle'
                                        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                Subtitle
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('video');
                                    if (!videoTabSeen) {
                                        setVideoTabSeen(true);
                                        localStorage.setItem('video-export-tab-seen', 'true');
                                    }
                                }}
                                className={`flex-1 py-2 px-4 text-sm font-medium transition-all border-b-2 -mb-px ${
                                    activeTab === 'video'
                                        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                Video
                                {!videoTabSeen && (
                                    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-orange-600 text-white rounded">
                                        New
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Subtitle Tab Content */}
                {activeTab === 'subtitle' && (
                    <div className="px-7 pt-5 pb-5">
                        <div className="mb-5">
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Format
                                </label>
                                <Select
                                    variant="modal"
                                    classNames={{
                                        control: () => 'md:p-1 !rounded-lg border font-bold text-sm dark:!bg-gray-800'
                                    }}
                                    value={{
                                        label: downloadOptions.find(o => o.format === selectedFormat)!.label,
                                        value: selectedFormat,
                                        description: downloadOptions.find(o => o.format === selectedFormat)!.description
                                    }}
                                    onChange={(selected: any) => setSelectedFormat(selected?.value || 'srt')}
                                    options={downloadOptions.map(o => ({
                                        label: o.label,
                                        value: o.format,
                                        description: o.description
                                    }))}
                                    formatOptionLabel={(option: any) => (
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{option.label}</span>
                                            <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                                {option.description}
                                            </span>
                                        </div>
                                    )}
                                    isSearchable={false}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                />
                            </div>
                        </div>

                        {hasStyles && selectedFormat !== 'ass' && selectedFormat !== 'json' && (
                            <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                <div className="flex items-start">
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0"
                                    />
                                    <p className="text-sm text-amber-800 dark:text-amber-200">
                                        {selectedFormat.toUpperCase()} format does not support custom styles. Use ASS format to preserve styling.
                                    </p>
                                </div>
                            </div>
                        )}

                        { languageOptions && (languageOptions.length > 1 || isBulk && languageOptions.length > 0) ? (
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Language</label>
                                <Select
                                    variant="modal"
                                    classNames={{
                                        control: (state) =>
                                            'md:p-1 !rounded-lg border  font-bold text-sm dark:!bg-gray-800'
                                    }}

                                    defaultValue={defaultLanguage ? [{label: getLanguageName(defaultLanguage), value: defaultLanguage}] : []}
                                    isSearchable={false}
                                    isMulti={true}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    menuPlacement="bottom"
                                    className="mb-4"
                                    placeholder="Select languages"
                                    isOptionDisabled={(option) => option.value == '*'}
                                    onChange={(selected) => setSelectedLanguages(selected ? selected.map(s => s.value) : [])}
                                    options={
                                        languageOptions.map(l => ({label: getLanguageName(l), value: l}))
                                    }/>
                            </div>
                        ) : null }


                        {selectedFormat !== 'json' && (<>
                        <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Options
                        </label>
                        <div className="mb-4 border border-gray-200 dark:border-gray-600 rounded-lg  dark:bg-gray-800">
                            <button
                                onClick={() => setShowOptions(!showOptions)}
                                className="text-sm text-gray-900 dark:text-gray-100 flex items-center w-full justify-between px-3 py-3"
                            >
                                <span>{showOptions ? 'Hide options' : 'Show options'}</span>
                                <svg
                                    className={`w-4 h-4  transform transition-transform ${showOptions ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M9 5l7 7-7 7"/>
                                </svg>

                            </button>

                            <div
                                className={` mx-4 space-y-4 transition-all duration-200 overflow-hidden ${showOptions ? 'max-h-96 opacity-100 mt-1 mb-4 px-1 py-1' : 'max-h-0 opacity-0 mt-0'}`}>
                                {selectedFormat === 'txt' ? (
                                    <div>
                                        <label className="flex items-center space-x-3">
                                            <input
                                                type="checkbox"
                                                checked={includeTimestamps}
                                                onChange={(e) => setIncludeTimestamps(e.target.checked)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Include timing information</span>
                                        </label>
                                        <div className="flex items-center space-x-3 mt-3">
                                            <label className="text-sm text-gray-600 dark:text-gray-400">Line spacing</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="5"
                                                value={lineSpacing}
                                                onChange={(e) => setLineSpacing(Number(e.target.value))}
                                                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                ) : selectedFormat === 'ass' ? (
                                    <div className="space-y-4">
                                        <label className="flex items-center space-x-3">
                                            <input
                                                type="checkbox"
                                                checked={closeSmallGaps}
                                                onChange={(e) => setCloseSmallGaps(e.target.checked)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                            />
                                            <span
                                                className="text-sm text-gray-600 dark:text-gray-400">Close subtitle gaps shorter than 1 second</span>
                                        </label>
                                        <label className="flex items-start space-x-3">
                                            <input
                                                type="checkbox"
                                                checked={embedFonts}
                                                onChange={(e) => setEmbedFonts(e.target.checked)}
                                                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">Embed fonts</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Includes all fonts directly in the subtitle file, no need to install fonts separately</span>
                                            </div>
                                        </label>
                                    </div>
                                ) : (
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={closeSmallGaps}
                                            onChange={(e) => setCloseSmallGaps(e.target.checked)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                        />
                                        <span
                                            className="text-sm text-gray-600 dark:text-gray-400">Close subtitle gaps shorter than 1 second</span>
                                    </label>
                                )}
                                {showRtlEmbeddingOption && (
                                    <label className="flex items-start space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={rtlEmbedding}
                                            onChange={(e) => handleRtlEmbeddingChange(e.target.checked)}
                                            className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                        />
                                        <div className="flex flex-col">
                                            <span className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                                Add right-to-left marks
                                                <Popover
                                                    delay={0}
                                                    trigger="hover"
                                                    placement="top"
                                                    portal
                                                    content={
                                                        <div className="w-72 text-sm text-gray-500 dark:text-gray-400 p-4">
                                                            <p>If you see small bars or boxes at the ends of subtitle lines, try changing your subtitle font.</p>
                                                            <p className="mt-2">Turning this option off also removes these artifacts, but punctuation may appear on the wrong side.</p>
                                                        </div>
                                                    }
                                                >
                                                    <div onClick={(e) => e.preventDefault()}>
                                                        <InfoIcon className="text-gray-300 dark:text-gray-600 ml-1 cursor-pointer hover:text-blue-700 dark:hover:text-blue-400 !w-4 !h-4"/>
                                                    </div>
                                                </Popover>
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Adds directional markers to help video players display right-to-left text correctly</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>
                        </>)}

                        <div className="pt-2">
                            <div>
                                <PrimaryButton
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                    className="flex items-center w-full justify-center"
                                >
                                    {
                                        !isDownloading && (
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                            </svg>
                                        )
                                    }
                                    <span>{isDownloading ? 'Preparing' : 'Download'}</span> {isDownloading && <Spinner className="ml-2 align-middle inline-block"/>}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                )}

                {/* Video Tab Content */}
                {videoPlayerRef && (
                    <VideoExportTab
                        job={job}
                        onBurnInStart={onBurnInStart}
                        handleCloseRef={handleCloseRef}
                        show={activeTab === 'video'}
                        languageOptions={availableLanguages}
                        defaultLanguage={defaultLanguage}
                        initialUpload={initialUpload}
                        videoPlayerRef={videoPlayerRef}
                        isFreeTrial={isFreeTrial}
                        onClose={onClose}
                        onUploadComplete={onUploadComplete}
                    />
                )}
            </div>
        </Modal>
    );
}
